import React, {useEffect, useState} from 'react';
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import ButtonLoader from "../../utils/components/ButtonLoader";
import {invokeDAGviaApiPost} from "../../Api/invokeDag";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import AirflowDagLogs from "../Components/airflow/dagLogs/AirflowDagLogs";
import Page from "../Components/Page";
import PeriodsDropdown from "../Components/Dropdowns/PeriodsDropdown";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import StacklineDataUnload from "./StacklineDataUnload";
import Checkbox from "../Components/Checkbox";
import {makeApiCall} from "../../Api/makeApiCall";
import {wrapValueInOptionStructure} from "../../utils/maybeAddLabelToOptions";

const StacklineProcess = () => {
    const DAAP_DAG_ID_TO_TRIGGER = "call_daap_hierarchical_model"
    const STACKLINE_MEASURES_DAG_ID_TO_TRIGGER = "generate_stackline_measures"
    const STACKLINE_PROMO_DAG_ID_TO_TRIGGER = "promo_stackline"
    const PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER = "publish_market_insights_stackline"
    const PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER = "publish_market_insights_stackline_views"
    const PI_SNAPSHOT_RUN_TYPE = "pi_snapshot"
    const RUN_TYPE_OPTIONS = [
        {value: "daap_hierarchy", label: "Hierarchy Inference"},
        {value: PI_SNAPSHOT_RUN_TYPE, label: "PI Snapshot"}
    ]

    const [dimProductPeriod, updateDimProductPeriod] = useState(undefined);
    const [publishViewPeriodSelection, updatePublishViewPeriodSelection] = useState(undefined);
    const [publishPeriodSelection, updatePublishPeriodSelection] = useState(undefined);
    const [publishViewMeasuresSource, setPublishViewMeasuresSource] = useState(undefined);
    const [publishMeasuresSource, setPublishMeasuresSource] = useState(undefined);
    const [publishViewDataset, setPublishViewDataset] = useState("STANDARD_SD_DM");
    const [publishViewProject, setPublishViewProject] = useState("shining-landing-763");
    const [isPublishMasterDimProductsOnly, updateIsPublishMasterDimProductsOnly] = useState(false);
    const [measuresRunType, updateMeasuresRunType] = useState("daap_hierarchy");
    const [publishRunType, updatePublishRunType] = useState("daap_hierarchy");
    const [piAsinTableSuffixOptions, setPiAsinTableSuffixOptions] = useState(undefined);
    const [measuresPiAsinTableSuffix, updateMeasuresPiAsinTableSuffix] = useState(undefined);

    useEffect(() => {
        makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/stacklinePiAsinTableSuffixes`)
            .then((suffixes) => setPiAsinTableSuffixOptions(Array.isArray(suffixes) ? suffixes.map(wrapValueInOptionStructure) : []));
    }, []);

    const runTypeLabel = (runType) => RUN_TYPE_OPTIONS.find(option => option.value === runType)?.label;
    const measuresRunTypeLabel = runTypeLabel(measuresRunType);
    const publishRunTypeLabel = runTypeLabel(publishRunType);
    const isMeasuresPiSnapshot = measuresRunType === PI_SNAPSHOT_RUN_TYPE;
    const measuresDagConf = isMeasuresPiSnapshot
        ? {'run_type': measuresRunType, 'pi_asin_dim_t_products_period': measuresPiAsinTableSuffix}
        : {'run_type': measuresRunType};

    const triggerDag = (dagId) => {
        return invokeDAGviaApiPost(dagId, {});
    };

    return (
        <Page name={"Stackline"}
              beta={true}
              breadCrumbs={<Breadcrumbs items={[{label: "Stackline"}, {label: "Stackline Process"}]}/>}
        >
            <Tabs>
                <TabList>
                    <Tab data-testid={'unload-stackline-data-tab-name'}>Unload from snowflake</Tab>
                    <Tab data-testid={'call-daap-hierarchical-model-tab-name'}>DAD Hierarchical</Tab>
                    <Tab data-testid={'promo-tab-name'}>Promo</Tab>
                    <Tab data-testid={'generate-stackline-measures-tab-name'}>Measures</Tab>
                    <Tab data-testid={'publish-stackline-measures-tab-name'}>Publish Measures</Tab>
                    <Tab data-testid={'publish-stackline-measures-views-tab-name'}>Publish Measures Views</Tab>
                </TabList>
                <TabPanel data-testid={'unload-stackline-data-tab-panel'}>
                    <StacklineDataUnload/>
                </TabPanel>
                <TabPanel data-testid={'call-daap-hierarchical-model-tab-panel'}>
                    <div className="publish-stackline-measures-inputs-for-dag-trigger">
                        <div className="generate-stackline-measures-period-selection">
                            <PeriodsDropdown
                                updatePeriod={updateDimProductPeriod}
                                selectedPeriod={dimProductPeriod}
                                graphqlQuery={'{ marketPeriodList }'}
                            />
                        </div>
                        <div className="publish-stackline-measures-trigger-dag-button">
                            <ButtonLoader
                                buttonTitle={"Trigger Hierarchical Model Dag"}
                                buttonHasModal
                                buttonClassName={"button primary align-bottom"}
                                modalText={`Are you sure you want to trigger ${DAAP_DAG_ID_TO_TRIGGER}?`}
                                axiosFunction={() => invokeDAGviaApiPost(DAAP_DAG_ID_TO_TRIGGER, {
                                    'period': dimProductPeriod
                                })}
                                toastTextFailure={`${DAAP_DAG_ID_TO_TRIGGER} DAG trigger failed with error`}
                                toastTextSuccess={`${DAAP_DAG_ID_TO_TRIGGER} DAG triggered successfully`}
                            />
                        </div>
                    </div>
                    <br/>
                    <AirflowDagLogs
                        processName={DAAP_DAG_ID_TO_TRIGGER}
                        includeDagName={false}
                    />
                </TabPanel>
                <TabPanel data-testid={'promo-tab-panel'}>
                    <br/>
                    <ButtonLoader
                        buttonTitle={"Trigger Stackline Promo Dag"}
                        buttonHasModal
                        buttonClassName={"button primary "}
                        modalText={`Are you sure you want to trigger ${STACKLINE_PROMO_DAG_ID_TO_TRIGGER}?`}
                        axiosFunction={() => triggerDag(STACKLINE_PROMO_DAG_ID_TO_TRIGGER)}
                        toastTextFailure={`${STACKLINE_PROMO_DAG_ID_TO_TRIGGER} DAG trigger failed with error`}
                        toastTextSuccess={`${STACKLINE_PROMO_DAG_ID_TO_TRIGGER} DAG triggered successfully`}
                    />
                    <br/>
                    <AirflowDagLogs
                        processName={STACKLINE_PROMO_DAG_ID_TO_TRIGGER}
                        includeDagName={false}
                    />
                </TabPanel>
                <TabPanel data-testid={'generate-stackline-measures-tab-panel'}>
                    <div className="publish-stackline-measures-inputs-for-dag-trigger generate-stackline-measures-inputs">
                        <div className="generate-stackline-measures-period-selection">
                            <SelectDropdown
                                id={'run_type_dropdown'}
                                dataTestId={'run_type_dropdown'}
                                inputLabel={"Run Type"}
                                onChange={(option) => updateMeasuresRunType(option.value)}
                                selectValue={measuresRunType}
                                selectLabel={measuresRunTypeLabel}
                                options={RUN_TYPE_OPTIONS}
                            />
                        </div>
                        {isMeasuresPiSnapshot && <div className="generate-stackline-measures-period-selection">
                            <SelectDropdown
                                id={'pi_asin_table_suffix_dropdown'}
                                dataTestId={'pi_asin_table_suffix_dropdown'}
                                inputLabel={"PI Engine Snapshot Period"}
                                onChange={(option) => updateMeasuresPiAsinTableSuffix(option.value)}
                                selectValue={measuresPiAsinTableSuffix}
                                options={piAsinTableSuffixOptions}
                            />
                        </div>}
                    </div>
                    <ButtonLoader
                        buttonTitle={"Trigger Stackline Measures Dag"}
                        disabled={isMeasuresPiSnapshot && !measuresPiAsinTableSuffix}
                        buttonHasModal
                        buttonClassName={"button primary "}
                        modalText={`Are you sure you want to trigger ${STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} with run type ${measuresRunTypeLabel}${isMeasuresPiSnapshot ? ` and table suffix ${measuresPiAsinTableSuffix}` : ''}?`}
                        axiosFunction={() => invokeDAGviaApiPost(STACKLINE_MEASURES_DAG_ID_TO_TRIGGER, measuresDagConf)}
                        toastTextFailure={`${STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} DAG trigger failed with error`}
                        toastTextSuccess={`${STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} DAG triggered successfully`}
                    />
                    <br/>
                    <AirflowDagLogs
                        processName={STACKLINE_MEASURES_DAG_ID_TO_TRIGGER}
                        includeDagName={false}
                    />
                </TabPanel>
                <TabPanel data-testid={'publish-stackline-measures-tab-panel'}>
                    <div className="publish-stackline-measures-inputs-for-dag-trigger">
                        <div className="generate-stackline-measures-period-selection">
                            <PeriodsDropdown
                                updatePeriod={updatePublishPeriodSelection}
                                selectedPeriod={publishPeriodSelection}
                                graphqlQuery={'{ marketPeriodList }'}
                            />
                        </div>
                        <div className="publish-stackline-measures-inputs generate-stackline-measures-period-selection">
                            <span className='input-group-label  span-height'>Measures Source:</span>
                            <input
                                id={'measures-source-input'}
                                data-testid={'publish-measures-source-input'}
                                className={'input-group-field'}
                                type='text'
                                value={publishMeasuresSource || ''}
                                onChange={(e) => setPublishMeasuresSource(e.target.value)}
                            />
                        </div>
                        <div className="generate-stackline-measures-period-selection">
                            <SelectDropdown
                                id={'publish_run_type_dropdown'}
                                dataTestId={'publish_run_type_dropdown'}
                                inputLabel={"Run Type"}
                                onChange={(option) => updatePublishRunType(option.value)}
                                selectValue={publishRunType}
                                selectLabel={publishRunTypeLabel}
                                options={RUN_TYPE_OPTIONS}
                            />
                        </div>
                        <div className="publish-stackline-measures-trigger-dag-button">
                            <ButtonLoader
                                buttonTitle={"Trigger Publish Stackline Measures Dag"}
                                buttonHasModal
                                buttonClassName={"button primary align-bottom"}
                                modalText={`Are you sure you want to trigger ${PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} with run type ${publishRunTypeLabel}?`}
                                axiosFunction={() => invokeDAGviaApiPost(PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER, {
                                    'period': publishPeriodSelection,
                                    'measures_sources': [publishMeasuresSource],
                                    'run_type': publishRunType
                                })}
                                toastTextFailure={`${PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} DAG trigger failed with error`}
                                toastTextSuccess={`${PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER} DAG triggered successfully`}
                            />
                        </div>
                    </div>
                    <AirflowDagLogs
                        processName={PUBLISH_STACKLINE_MEASURES_DAG_ID_TO_TRIGGER}
                        includeDagName={false}
                    />
                </TabPanel>
                <TabPanel data-testid={'publish-stackline-measures-views-tab-panel'}>
                    <div className="publish-stackline-measures-inputs-for-dag-trigger">
                        <div className="generate-stackline-measures-period-selection">
                            <PeriodsDropdown
                                updatePeriod={updatePublishViewPeriodSelection}
                                selectedPeriod={publishViewPeriodSelection}
                                graphqlQuery={'{ marketPeriodList }'}
                            />
                        </div>
                        <div className="publish-stackline-measures-inputs generate-stackline-measures-period-selection">
                            <span className='input-group-label  span-height'>Source Prefix:</span>
                            <input
                                id={'measures-source-input'}
                                data-testid={'publish-measures-source-input'}
                                className={'input-group-field'}
                                type='text'
                                value={publishViewMeasuresSource || ''}
                                onChange={(e) => setPublishViewMeasuresSource(e.target.value)}
                            />
                        </div>
                        <div className="publish-stackline-measures-inputs generate-stackline-measures-period-selection">
                            <Checkbox
                                id={'master-dim-products_only-checkbox'}
                                checked={isPublishMasterDimProductsOnly}
                                labelText='Publish Combined Dim Products Views Only'
                                onChange={() => updateIsPublishMasterDimProductsOnly(!isPublishMasterDimProductsOnly)}
                            />
                        </div>
                    </div>
                    <div className="publish-stackline-measures-inputs-for-dag-trigger">
                        <div className="publish-stackline-measures-inputs generate-stackline-measures-period-selection">
                            <span className='input-group-label  span-height'>Dest Project:</span>
                            <input
                                id={'measures-source-input'}
                                data-testid={'publish-measures-dest-project'}
                                className={'input-group-field'}
                                type='text'
                                value={publishViewProject || ''}
                                onChange={(e) => setPublishViewProject(e.target.value)}
                            />
                        </div>
                        <div className="publish-stackline-measures-inputs generate-stackline-measures-period-selection">
                            <span className='input-group-label span-height'>Dest Dataset:</span>
                            <input
                                id={'measures-source-input'}
                                data-testid={'publish-measures-dest-dataset'}
                                className={'input-group-field'}
                                type='text'
                                value={publishViewDataset || ''}
                                onChange={(e) => setPublishViewDataset(e.target.value)}
                            />
                        </div>

                        <div className="publish-stackline-measures-trigger-dag-button-margin">
                            <ButtonLoader
                                buttonTitle={"Trigger Stackline Publish View Dag"}
                                buttonHasModal
                                buttonClassName={"button primary  align-bottom"}
                                modalText={`Are you sure you want to trigger ${PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER}?`}
                                axiosFunction={() => invokeDAGviaApiPost(PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER, {
                                    'period': publishViewPeriodSelection.replace('-', '_'),
                                    'source_table_prefix': publishViewMeasuresSource,
                                    'publish_dataset_id': publishViewDataset,
                                    'publish_project_id': publishViewProject,
                                    'master_dim_products_only': isPublishMasterDimProductsOnly
                                })}
                                toastTextFailure={`${PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER} DAG trigger failed with error`}
                                toastTextSuccess={`${PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER} DAG triggered successfully`}
                            />
                        </div>
                    </div>
                    <AirflowDagLogs
                        processName={PUBLISH_STACKLINE_MEASURES_VIEWS_DAG_ID_TO_TRIGGER}
                        includeDagName={false}
                    />
                </TabPanel>
            </Tabs>
        </Page>
    );
};

export default StacklineProcess;